import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useGetMyClassGroupsLinkedToContent } from '@/api/gen/classroom/classroom.gen';
import { ChevronDownIcon, GearIcon } from '@/icons';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import { ClassroomSelectorAvatarLabel, ClassroomSelectorManageItem, ClassroomSelectorMenuItem } from '@/shared/features/classroom/components/ClassroomSelectorMenuPrimitives';
import { useClassroomId } from '@/shared/hooks';
import { selectData } from '@/shared/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuTrigger, TooltipArrow, TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from '@/ui';
import { cn } from '@/utils';
import { useQuizsetId } from '../../hooks';
const ALL_CLASSROOMS_AVATAR_COLOR = '#6758FF';
const ALL_CLASSROOMS_AVATAR_STYLE = {
  backgroundColor: ALL_CLASSROOMS_AVATAR_COLOR
};
export function ClassroomSelector() {
  const {
    t
  } = useTranslation(['lms']);
  const router = useRouter();
  const classroomId = useClassroomId();
  const quizsetId = useQuizsetId();
  const {
    data: classrooms,
    isSuccess
  } = useGetMyClassGroupsLinkedToContent({
    contentId: quizsetId,
    contentType: 'QUIZSET'
  }, {
    query: {
      select: selectData,
      staleTime: 60_000,
      enabled: Boolean(quizsetId)
    }
  });
  const currentClassroom = useMemo(() => classrooms?.find(item => item.classGroupId === classroomId), [classroomId, classrooms]);
  const isAllClassroomsSelected = !classroomId;
  const handleSelectClassroom = (nextClassroomId: string | null) => {
    if ((nextClassroomId ?? null) === (classroomId ?? null)) return;
    const {
      classroomId: _omit,
      ...restQuery
    } = router.query;
    const nextQuery = nextClassroomId ? {
      ...restQuery,
      classroomId: nextClassroomId
    } : restQuery;
    void router.replace({
      pathname: router.pathname,
      query: nextQuery
    }, undefined, {
      shallow: true
    });
  };

  // 이 퀴즈에 더 이상 연결돼 있지 않은 학급이 URL 에 남아있으면 "전체"로 fallback.
  // 학급 연결 해제 후 같은 URL 로 돌아오는 경로를 깨끗하게 만든다.
  useEffect(() => {
    if (!isSuccess || !classroomId || currentClassroom) return;
    handleSelectClassroom(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, classroomId, currentClassroom]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPointerOver, setIsPointerOver] = useState(false);

  // Tooltip 은 마우스 포인터가 트리거 위에 있을 때만 노출. 드롭다운이 열려있거나
  // 포커스만 잡힌 경우(키보드/드롭다운 close 후 포커스 복귀)에는 숨겨 잘못된 표시를 막는다.
  const isTooltipOpen = isPointerOver && !isDropdownOpen;
  return <div className="mb-md flex flex-col gap-md" data-sentry-component="ClassroomSelector" data-sentry-source-file="ClassroomSelector.tsx">
      <TooltipProvider delayDuration={120} data-sentry-element="TooltipProvider" data-sentry-source-file="ClassroomSelector.tsx">
        <TooltipRoot open={isTooltipOpen} data-sentry-element="TooltipRoot" data-sentry-source-file="ClassroomSelector.tsx">
          <DropdownMenu modal={false} onOpenChange={setIsDropdownOpen} data-sentry-element="DropdownMenu" data-sentry-source-file="ClassroomSelector.tsx">
            <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="ClassroomSelector.tsx">
              <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="ClassroomSelector.tsx">
                <button onPointerEnter={() => setIsPointerOver(true)} onPointerLeave={() => setIsPointerOver(false)} className="flex h-[52px] w-full items-center gap-2xs rounded-sm border border-alternative bg-default px-sm py-xs transition-shadow hover:shadow-xs">
                  <ClassroomSelectorAvatarLabel text={currentClassroom ? currentClassroom.name : t('lms:lms.classroom.allLabel')} avatarSize="sm" avatarColorSeed={currentClassroom?.classGroupId} avatarText={currentClassroom ? undefined : t('lms:lms.classroom.allAvatarLabel')} avatarStyle={currentClassroom ? undefined : ALL_CLASSROOMS_AVATAR_STYLE} textClassName="text-body-lg-bold text-default" data-sentry-element="ClassroomSelectorAvatarLabel" data-sentry-source-file="ClassroomSelector.tsx" />
                  <ChevronDownIcon size="xs" className="ml-auto shrink-0 fill-default" data-sentry-element="ChevronDownIcon" data-sentry-source-file="ClassroomSelector.tsx" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <DropdownMenuContent align="start" sideOffset={4} className="max-h-[320px] w-[194px] min-w-[160px] max-w-[240px] rounded-sm border border-assistive bg-default px-xs py-3xs shadow-lg" data-sentry-element="DropdownMenuContent" data-sentry-source-file="ClassroomSelector.tsx">
              <ClassroomSelectorMenuItem selected={isAllClassroomsSelected} onSelect={() => handleSelectClassroom(null)} data-sentry-element="ClassroomSelectorMenuItem" data-sentry-source-file="ClassroomSelector.tsx">
                <ClassroomSelectorAvatarLabel text={t('lms:lms.classroom.allLabel')} avatarSize="sm" avatarText={t('lms:lms.classroom.allAvatarLabel')} avatarStyle={ALL_CLASSROOMS_AVATAR_STYLE} textClassName={cn('flex-1 text-left', isAllClassroomsSelected && 'text-body-sm-semibold text-strong')} showCheck={isAllClassroomsSelected} data-sentry-element="ClassroomSelectorAvatarLabel" data-sentry-source-file="ClassroomSelector.tsx" />
              </ClassroomSelectorMenuItem>
              {classrooms?.map(classroom => {
              const isCurrent = classroom.classGroupId === classroomId;
              return <ClassroomSelectorMenuItem key={classroom.classGroupId} selected={isCurrent} onSelect={() => handleSelectClassroom(classroom.classGroupId)}>
                    <ClassroomSelectorAvatarLabel text={classroom.name} avatarSize="sm" avatarColorSeed={classroom.classGroupId} textClassName={cn('flex-1 text-left', isCurrent && 'text-body-sm-semibold text-strong')} showCheck={isCurrent} />
                  </ClassroomSelectorMenuItem>;
            })}
              <DropdownMenuSeparator className="-mx-xs my-3xs bg-alternative" data-sentry-element="DropdownMenuSeparator" data-sentry-source-file="ClassroomSelector.tsx" />
              <ClassroomSelectorManageItem label={t('lms:lms.classroom.manageMyClassrooms')} icon={<GearIcon size="xs" />} onSelect={() => {
              void router.push(CLASSROOM_ROUTES.list());
            }} data-sentry-element="ClassroomSelectorManageItem" data-sentry-source-file="ClassroomSelector.tsx" />
            </DropdownMenuContent>
          </DropdownMenu>
          <TooltipContent side="bottom" align="center" sideOffset={6} className="rounded-xs border-none bg-opacity-black-9 p-2xs text-body-sm-semibold text-inverse shadow-lg" data-sentry-element="TooltipContent" data-sentry-source-file="ClassroomSelector.tsx">
            <TooltipArrow width={10} height={6} className="fill-opacity-black-9" data-sentry-element="TooltipArrow" data-sentry-source-file="ClassroomSelector.tsx" />
            {t('lms:lms.classroom.selectorTooltip')}
          </TooltipContent>
        </TooltipRoot>
      </TooltipProvider>
    </div>;
}